import { useParams, Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { getWorkById, getRelatedWorks, workTypeMap } from "@/mocks/works";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";

export default function WorkDetailPage() {
  const { id = "" } = useParams();
  const work = getWorkById(id);

  useSEO({
    title: work ? `${work.title} - 小泽玛利亚影视作品详情` : "作品未找到 - 小泽玛利亚影视作品",
    description: work
      ? `${work.title}（${work.year}·${workTypeMap[work.type]}）：${work.synopsis}`
      : "该作品暂时无法查看。",
    keywords: work
      ? `小泽玛利亚,${work.title},${workTypeMap[work.type]},影视作品`
      : "小泽玛利亚,影视作品",
  });

  if (!work) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-6 py-20 text-center">
        <i className="ri-film-line text-5xl text-primary-400"></i>
        <h1 className="font-heading text-2xl font-semibold text-foreground-950 mt-4">作品不存在</h1>
        <p className="text-sm text-foreground-600 mt-2">这部作品可能已被移除或链接有误。</p>
        <Link
          to="/works"
          className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回作品列表
        </Link>
      </div>
    );
  }

  const related = getRelatedWorks(work, 3);

  return (
    <div className="animate-fade-up mx-auto max-w-content px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "影视作品", to: "/works" }, { label: work.title }]} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 mt-5">
        <div className="lg:col-span-1">
          <div className="w-full max-w-[320px] mx-auto lg:mx-0 aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200">
            <img
              src={work.cover}
              alt={work.title}
              title={`${work.title} - 小泽玛利亚影视作品封面`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">
              {workTypeMap[work.type]}
            </span>
            <span className="text-xs text-foreground-500">{work.year} 年</span>
            <span className="text-xs text-foreground-500">{work.duration}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mb-5">
            {work.title}
          </h1>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-6 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <div className="flex gap-2 text-sm">
              <dt className="text-foreground-500 shrink-0">导演：</dt>
              <dd className="text-foreground-900">{work.director}</dd>
            </div>
            <div className="flex gap-2 text-sm">
              <dt className="text-foreground-500 shrink-0">上映年份：</dt>
              <dd className="text-foreground-900">{work.year} 年</dd>
            </div>
            <div className="flex gap-2 text-sm sm:col-span-2">
              <dt className="text-foreground-500 shrink-0">主要演员：</dt>
              <dd className="text-foreground-900">{work.cast}</dd>
            </div>
          </dl>

          <section className="mb-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-book-open-line text-primary-600"></i>
              作品简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.synopsis}</p>
          </section>

          <section className="mb-6 bg-primary-50 border border-primary-100 rounded-lg p-5">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-user-star-line text-primary-600"></i>
              小泽玛利亚的参与
            </h2>
            <p className="text-sm font-medium text-primary-800 mb-2">{work.roleName}</p>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.roleDesc}</p>
          </section>

          <div className="flex flex-wrap gap-2">
            {work.tags.map((t) => (
              <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">
                #{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <section className="mt-14 md:mt-16 pt-8 border-t border-background-200">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5 flex items-center gap-2">
          <i className="ri-magic-line text-primary-600"></i>
          相关作品推荐
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-5">
          {related.map((item) => (
            <WorkCard key={item.id} item={item} />
          ))}
        </div>
        <Link
          to="/works"
          className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
        >
          查看全部影视作品
          <i className="ri-arrow-right-line"></i>
        </Link>
      </section>
    </div>
  );
}